import { useState } from 'react';
import { flexRender, Table as TanStackTable } from '@tanstack/react-table';
import { TableHead, TableRow, TableCell, TableSortLabel, Box, IconButton } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { ColumnFiltersState } from '@tanstack/react-table';
import FilterMenu from './FilterMenu';

export interface FilterOption {
    value: string;
    label: string;
    backgroundColor?: string;
}

export enum FilterType {
    TEXT = 'text',
    SELECT = 'select',
    COMBOBOX = 'combobox',
}

export interface FilterConfig {
    id: string;
    label: string;
    type: FilterType;
    options?: FilterOption[];
    placeholder?: string;
    columnId?: string;
    maxWidth?: string;
    hideAllOption?: boolean;
    permanent?: boolean;
}

interface TableHeaderProps<TData extends object> {
    table: TanStackTable<TData>;
    filters?: FilterConfig[];
    columnFilters?: ColumnFiltersState;
    onFiltersChange?: (filters: ColumnFiltersState) => void;
}

export type ColumnFilterWithDisplayLabel = ColumnFiltersState[number] & {
    displayLabel?: string;
};

/**
 * TableHeader component that displays the table header with filter options if provided.
 *
 * @template TData
 * @param {TableHeaderProps<TData>} props
 * @returns {JSX.Element}
 */
const TableHeader = <TData extends object>(props: TableHeaderProps<TData>) => {
    const { table, filters = [], columnFilters = [], onFiltersChange } = props;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeColumn, setActiveColumn] = useState<string | null>(null);

    // Handles the click event for the filter button
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>, columnId: string) => {
        setAnchorEl(event.currentTarget);
        setActiveColumn(columnId);
    };

    // Handles the close event for the filter menu
    const handleClose = () => {
        setAnchorEl(null);
        setActiveColumn(null);
    };

    // Handles the selection of a filter option
    const handleFilterOptionSelect = (filterId: string, filterValue: string, displayLabel: string) => {
        if (!onFiltersChange) return;

        const newFilters = columnFilters.filter((f) => f.id !== filterId);
        const updatedFilters =
            filterValue && filterValue !== 'all' ? [...newFilters, { id: filterId, value: filterValue, displayLabel }] : newFilters;

        onFiltersChange(updatedFilters);
        handleClose();
    };

    // Finds the filter for a given column
    const getFilterForColumn = (columnId: string) => {
        return filters.find((filter) => filter.columnId === columnId || filter.id === columnId);
    };

    // Gets the value of a filter for a given filter ID
    const getFilterValue = (filterId: string): string => {
        const filter = columnFilters.find((f) => f.id === filterId);

        return filter?.value ? (filter.value as string) : '';
    };

    // Checks if a filter is active
    const isFilterActive = (filter: FilterConfig) => {
        const value = getFilterValue(filter.id);

        return value !== '' && value !== 'all';
    };

    return (
        <TableHead
            sx={{
                bgcolor: 'secondary.light',
                color: 'secondary.contrastText',
                fontWeight: 500,
                fontSize: '0.875rem',
            }}
        >
            {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const filter = getFilterForColumn(header.id);
                        const isFilterable = typeof filter !== 'undefined' && filter.options && filter?.options.length > 0;
                        const isActive = isFilterable && isFilterActive(filter!);

                        return (
                            <TableCell
                                key={header.id}
                                sx={{
                                    fontWeight: 'bold',
                                    width: header.getSize(),
                                    maxWidth: header.column.columnDef.maxSize || '200px',
                                }}
                            >
                                {header.isPlaceholder ? null : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {header.column.getCanSort() ? (
                                                <TableSortLabel
                                                    active={!!header.column.getIsSorted()}
                                                    direction={header.column.getIsSorted() || 'asc'}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableSortLabel>
                                            ) : (
                                                flexRender(header.column.columnDef.header, header.getContext())
                                            )}
                                        </Box>
                                        {isFilterable && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleFilterClick(e, header.id)}
                                                color={isActive ? 'primary' : 'default'}
                                                aria-label={`Filter by ${filter?.label.toLowerCase()}`}
                                            >
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                )}
                                {isFilterable && activeColumn === header.id && filter && (
                                    <FilterMenu
                                        anchorEl={anchorEl}
                                        filter={filter}
                                        columnFilters={columnFilters}
                                        onClose={handleClose}
                                        onFilterChange={handleFilterOptionSelect}
                                    />
                                )}
                            </TableCell>
                        );
                    })}
                </TableRow>
            ))}
        </TableHead>
    );
};

export default TableHeader;
